import type { FieldPath } from "react-hook-form";
import {
	type AppointmentBookingSchema,
	isPatientLocationKey,
	PATIENT_DETAILS_SCHEMA,
	PATIENT_LOCATION_KEYS,
} from "./form";

/**
 * Which booking-form step renders which part of the schema.
 *
 * The Zod namespaces and the wizard steps stopped lining up once the visit location
 * moved to step 2 and the booking source moved to step 1: `additionalSettings` is now
 * split across steps 1 and 3, and `patientDetails` across steps 1 and 2. Every consumer
 * that has to answer "which step owns this field" — the per-step `trigger` lists and the
 * error-to-step lookup below — derives from this one table, so they cannot drift apart.
 *
 * Indexed by the stepper's own step order (see the `steps` array in New.tsx):
 *   0 Patient Profile · 1 Appointment Scheduling · 2 Additional Settings · 3 Review
 *
 * `paths` is what the step validates on "Save & Next", and it must list only what the
 * step actually puts on screen. A step that validates more than it renders refuses to
 * advance over a field the admin cannot see from there — which is exactly how choosing
 * "Lainnya" as the referral source, a step-1 field, came to deadlock step 3.
 *
 * `catchAll` is for the error-to-step lookup only, never for validation. It lets a step
 * own the leftovers of a namespace it shares, so a field nobody thought to list still
 * resolves to a plausible step instead of falling back to step 1. Longest prefix wins, so
 * the explicit step-1 entries below beat step 3's `additionalSettings` catch-all.
 */
export const BOOKING_STEPS = [
	// Step 1 — patient profile, contact, and the booking source.
	{
		paths: [
			...Object.keys(PATIENT_DETAILS_SCHEMA.shape)
				.filter((key) => !isPatientLocationKey(key))
				.map((key) => `patientDetails.${key}`),
			"contactInformation",
			"additionalSettings.referralSource",
			"additionalSettings.customReferralSource",
			"additionalSettings.fisiohomePartnerBooking",
			"additionalSettings.fisiohomePartnerName",
			"additionalSettings.customFisiohomePartnerName",
			"additionalSettings.notes",
		],
		catchAll: [],
	},
	// Step 2 — scheduling, plus the visit location half of patientDetails.
	{
		paths: [
			"appointmentScheduling",
			...PATIENT_LOCATION_KEYS.map((key) => `patientDetails.${key}`),
		],
		catchAll: [],
	},
	// Step 3 — the fields it renders, listed one by one rather than as the whole
	// `additionalSettings` namespace: half of that namespace is asked on step 1 now.
	{
		paths: [
			"additionalSettings.voucherCode",
			"additionalSettings.enablePaymentGateway",
			"additionalSettings.paymentExpiryMinutes",
			"additionalSettings.bypassSlotConflict",
			"additionalSettings.admins",
		],
		// Anything else left in the namespace still reports as this step's.
		catchAll: ["additionalSettings"],
	},
	// Step 4 — review renders no fields of its own, and is where the whole schema is
	// checked at last: nothing else validates across steps.
	{ paths: [], catchAll: [] },
] as const satisfies readonly {
	paths: readonly string[];
	catchAll: readonly string[];
}[];

/** The paths a given step should `trigger`, typed for react-hook-form. */
export const stepPaths = (step: number) =>
	(BOOKING_STEPS[step]?.paths ??
		[]) as unknown as FieldPath<AppointmentBookingSchema>[];

/**
 * The step that renders `path`, by longest-prefix match — so
 * "additionalSettings.customReferralSource" resolves to step 1 even though step 3
 * claims the whole "additionalSettings" namespace.
 *
 * Falls back to step 0 for a path no step claims: stranding the admin on a step with
 * nothing to fix is worse than sending them to the beginning.
 */
export const stepOfErrorPath = (path: string): number => {
	let bestStep = 0;
	let bestLength = -1;

	BOOKING_STEPS.forEach(({ paths, catchAll }, step) => {
		for (const owned of [...paths, ...catchAll]) {
			if (path !== owned && !path.startsWith(`${owned}.`)) continue;
			if (owned.length <= bestLength) continue;

			bestLength = owned.length;
			bestStep = step;
		}
	});

	return bestStep;
};

/** One step's worth of blockers, as the step-buttons panel renders them. */
export type BlockingStep = {
	/** Index into the stepper's steps, or -1 when no field could be pinned down. */
	step: number;
	label: string;
	/** `visitIndex` is set when the message is a visit's Mitra reason, so it can open that visit. */
	messages: { text: string; visitIndex?: number }[];
};

export type BookingFormError = {
	/** Dotted path of the offending field, e.g. "additionalSettings.customReferralSource". */
	path: string;
	message: string;
	/** Index of the step that renders it, per stepOfErrorPath. */
	step: number;
};

/**
 * Recursively walk a react-hook-form errors object and return every human-readable
 * error it holds, each tagged with the step that owns it.
 *
 * The step is what makes this worth having: the errors that block a submit render
 * inline on the step that owns them, and by then the admin is usually somewhere else —
 * on Review, or on a step whose "Next" button just silently refuses. Without a step to
 * name and jump to, the only feedback is a button that does nothing.
 */
export const allBookingFormErrors = (
	errors: unknown,
	path = "",
): BookingFormError[] => {
	if (!errors || typeof errors !== "object") return [];

	const node = errors as Record<string, unknown>;
	if (typeof node.message === "string" && node.message) {
		return [{ path, message: node.message, step: stepOfErrorPath(path) }];
	}

	return Object.entries(node).flatMap(([key, value]) =>
		// react-hook-form hangs its own bookkeeping (`ref`, `types`) alongside the
		// nested fields; walking into it would yield a path no step can own.
		key === "ref" || key === "types"
			? []
			: allBookingFormErrors(value, path ? `${path}.${key}` : key),
	);
};

/** The first error to report on, for a toast that can only carry one. */
export const firstBookingFormError = (
	errors: unknown,
): BookingFormError | undefined => allBookingFormErrors(errors)[0];

/**
 * The blocking errors grouped by the step that owns them, in step order — what the
 * Review screen needs to say "these two steps still need work" before the admin finds
 * out by pressing a button that refuses.
 */
export const bookingErrorsByStep = (
	errors: unknown,
): { step: number; errors: BookingFormError[] }[] => {
	const byStep = new Map<number, BookingFormError[]>();

	for (const error of allBookingFormErrors(errors)) {
		const bucket = byStep.get(error.step);
		bucket ? bucket.push(error) : byStep.set(error.step, [error]);
	}

	return [...byStep.entries()]
		.sort(([a], [b]) => a - b)
		.map(([step, stepErrors]) => ({ step, errors: stepErrors }));
};
